/**
 * "Send test notification" row of the AlertRuleDialog (S24 review): alerts
 * are fire-and-forget by nature, so the only way to trust the OS side is to
 * exercise it once. Reuses the SAME `dashboards:notify-alert` bridge the real
 * alert path uses (S12) — a refused/unsupported notify degrades to
 * `{ sent: false }` and is surfaced honestly instead of pretending success.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toaster';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import type { StatTile, TileAlertRule } from '../types/dashboardTypes';

type Props = {
  dashboardId: string;
  tile: StatTile;
  rule: TileAlertRule;
  /** `false` = the probe says this OS can't show notifications at all. */
  supported: boolean | null;
};

export function AlertNotificationTest({ dashboardId, tile, rule, supported }: Props) {
  const { track } = useAnalytics();
  const [sent, setSent] = useState(false);

  const send = async () => {
    const result = await window.electronAPI?.dashboards
      ?.notifyAlert({
        dashboardId,
        tileId: tile.id,
        title: tile.title,
        body: `Test alert — notifies when the value is ${rule.operator} ${rule.threshold}`,
        background: rule.background,
      })
      .catch(() => ({ sent: false }));
    setSent(result?.sent === true);
    if (result?.sent) {
      // Success-side only (repo rule) — a refused notify is a toast, not an event.
      track('feature:dashboard_alert_test_notification');
      return;
    }
    toast.warning({
      title: 'No notification was shown',
      description: 'The system refused it — the in-app badge still works.',
    });
  };

  return (
    <div className="flex items-center gap-2">
      {/* WHY tooltip on a span wrapper: a disabled Button's pointer-events-none
          swallows its own `title` (S24 F19). */}
      <span title={supported === false ? "OS notifications aren't available on this system" : undefined}>
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs disabled:bg-muted disabled:text-muted-foreground"
          disabled={supported === false}
          onClick={() => void send()}
          data-testid="alert-rule-test-notification-button"
        >
          Send test notification
        </Button>
      </span>
      {sent && (
        <span
          className="text-[11px] text-muted-foreground"
          aria-live="polite"
          data-testid="alert-rule-test-notification-sent"
        >
          Sent — check your notification centre.
        </span>
      )}
    </div>
  );
}
